import React, { createContext, useContext, useEffect } from 'react';
import { useUndoRedo } from '../hooks/useUndoRedo';
import type { UndoAction } from '../hooks/useUndoRedo';
import { toast } from 'sonner';

interface UndoRedoContextValue {
  pushAction: (action: UndoAction) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
}

const UndoRedoContext = createContext<UndoRedoContextValue | null>(null);

export const useUndoRedoContext = (): UndoRedoContextValue => {
  const context = useContext(UndoRedoContext);
  if (!context) {
    throw new Error('useUndoRedoContext must be used within UndoRedoProvider');
  }
  return context;
};

export const UndoRedoProvider = ({ children }: { children: React.ReactNode }) => {
  const { pushAction, undo, redo, canUndo, canRedo, clearHistory } = useUndoRedo();

  // Global Ctrl+Z / Ctrl+Y keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isCtrl = e.ctrlKey || e.metaKey;

      if (isCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          await undo();
          toast.info('Acao desfeita', { duration: 2000 });
        }
        return;
      }

      if (isCtrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (canRedo) {
          await redo();
          toast.info('Acao refeita', { duration: 2000 });
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo]);

  return (
    <UndoRedoContext.Provider value={{ pushAction, undo, redo, canUndo, canRedo, clearHistory }}>
      {children}
    </UndoRedoContext.Provider>
  );
};
